/**
 * Reusable Column Filter Dropdown Component
 * Used for filtering table columns with checkboxes.
 * Position via useFilterPopupPortal (parent) — do not self-position.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { FILTER_POPUP_CLASS } from '../../../hooks/useFilterPopupPortal';

export const ColumnFilterDropdown = ({
  isOpen,
  options,
  selectedValues,
  onSelect,
  onClose,
  maxHeight = '60vh',
  minWidth = '16rem',
  maxWidth = '20rem',
}) => {
  const [tempSelected, setTempSelected] = useState([...selectedValues]);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const [effectiveMaxHeight, setEffectiveMaxHeight] = useState(maxHeight);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTempSelected([...selectedValues]);
    }
  }, [isOpen, selectedValues]);

  // UX: no auto-close on outside tap / scroll; close via X / Clear / Apply.

  useLayoutEffect(() => {
    if (!isOpen) return;

    const recalculate = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      const scrollEl = dropdown.querySelector('.filter-options-scroll');
      if (!scrollEl) return;

      const margin = 8;
      const vv = window.visualViewport;
      const viewportH =
        vv?.height ?? window.innerHeight ?? document.documentElement.clientHeight;
      const dropdownRect = dropdown.getBoundingClientRect();

      if (dropdownRect.bottom > viewportH - margin) {
        const overflowY = dropdownRect.bottom - (viewportH - margin);
        const scrollRect = scrollEl.getBoundingClientRect();
        const newScrollHeight = Math.max(120, scrollRect.height - overflowY);
        setEffectiveMaxHeight(`${newScrollHeight}px`);
      } else {
        setEffectiveMaxHeight(maxHeight);
      }
    };

    recalculate();
    window.addEventListener('resize', recalculate);
    window.addEventListener('orientationchange', recalculate);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', recalculate);
      vv.addEventListener('scroll', recalculate);
    }

    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('orientationchange', recalculate);
      if (vv) {
        vv.removeEventListener('resize', recalculate);
        vv.removeEventListener('scroll', recalculate);
      }
    };
  }, [isOpen, maxHeight]);

  if (!isOpen) return null;

  const getOptionValue = (option) => {
    return option && typeof option === 'object' && option.value !== undefined ? option.value : option;
  };

  const getOptionLabel = (option) => {
    return option && typeof option === 'object' && option.label !== undefined ? option.label : option;
  };

  const filteredOptions = options.filter((option) => {
    const label = getOptionLabel(option);
    return label.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleTempSelect = (value) => {
    const actualValue = getOptionValue(value);
    setTempSelected((prev) => {
      if (prev.includes(actualValue)) {
        return prev.filter((v) => v !== actualValue);
      }
      return [...prev, actualValue];
    });
  };

  const handleSelectAll = () => {
    if (tempSelected.length === filteredOptions.length) {
      setTempSelected([]);
    } else {
      setTempSelected(filteredOptions.map((option) => getOptionValue(option)));
    }
  };

  const handleApply = () => {
    if (typeof onSelect !== 'function') {
      onClose();
      return;
    }

    selectedValues.forEach((value) => {
      if (!tempSelected.includes(value)) {
        onSelect(value);
      }
    });

    tempSelected.forEach((value) => {
      if (!selectedValues.includes(value)) {
        onSelect(value);
      }
    });

    onClose();
  };

  const handleClear = () => {
    setTempSelected([]);
    setSearchTerm('');
    if (typeof onSelect === 'function') {
      selectedValues.forEach((value) => {
        onSelect(value);
      });
    }
    onClose();
  };

  return (
    <div
      ref={dropdownRef}
      className={`${FILTER_POPUP_CLASS} z-50 bg-white border rounded-md shadow-lg w-full`}
      style={{
        minWidth,
        maxWidth,
        maxHeight: '80vh',
      }}
    >
      <div className="flex flex-col">
        <div className="sticky top-0 z-10 bg-white p-2 border-b">
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              placeholder="Search..."
              className="h-8 flex-1 min-w-0 rounded border border-gray-300 px-2.5 text-sm text-gray-900 font-normal focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              aria-label="Close filter"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500/30"
            >
              <FiX className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="sticky top-[49px] bg-white p-2 border-b">
          <label className="flex items-center gap-2 p-1">
            <input
              type="checkbox"
              checked={tempSelected.length === filteredOptions.length && filteredOptions.length > 0}
              onChange={handleSelectAll}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-900 font-normal">Select All</span>
          </label>
        </div>

        <div
          className="p-2 pb-16 filter-options-scroll"
          style={{
            maxHeight: effectiveMaxHeight,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
          }}
          onScroll={(e) => e.stopPropagation()}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const optionValue = getOptionValue(option);
              const optionLabel = getOptionLabel(option);
              const isSelected = tempSelected.includes(optionValue);

              return (
                <label
                  key={index}
                  className="flex items-center gap-2 p-1 hover:bg-gray-50"
                  style={{ height: '24px' }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleTempSelect(option)}
                    className={`rounded ${isSelected ? 'border-red-500 text-red-600' : 'border-gray-300'}`}
                  />
                  <span
                    className={`text-sm ${isSelected ? 'text-red-600 font-medium' : 'text-gray-900 font-normal'} truncate flex-1 text-left`}
                    title={optionLabel.length > 30 ? optionLabel : ''}
                  >
                    {optionLabel.length > 30 ? `${optionLabel.substring(0, 30)}...` : optionLabel}
                  </span>
                </label>
              );
            })
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">No matching options found</div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
